/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * Invitation interface
 */
export interface Invitation {
  id: string;
  organization_id: string;
  organization_name?: string;
  team_id?: string;
  team_name?: string;
  invitee_email: string;
  inviter_id: string;
  inviter_name?: string;
  role?: string;
  role_id?: string;
  role_name?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';
  personal_message?: string;
  decline_reason?: string;
  token?: string;
  expires_at?: string;
  created_at?: string;
  accepted_at?: string;
  declined_at?: string;
  cancelled_at?: string;
}

/**
 * Create Invitation Request
 */
export interface CreateInvitationRequest {
  email: string;
  role: string;
  team_id?: string;
  message?: string;
  expires_at?: string;
}

/**
 * Extended Create Invitation Request
 */
export interface CreateInvitationExtendedRequest {
  invitee_email: string;
  role?: string;
  role_id?: string;
  team_id?: string;
  personal_message?: string;
  expires_at?: string;
}

/**
 * Bulk Invite Result Item - one entry per requested email
 */
export interface BulkInviteResultItem {
  index: number;
  invitee_email: string;
  invitation?: Invitation;
  success: boolean;
  error?: string;
}

/**
 * Bulk Invite Result - top-level response from the batch endpoint
 */
export interface BulkInviteResult {
  successful_invitations: BulkInviteResultItem[];
  failed_invitations: BulkInviteResultItem[];
  total_requested: number;
  successful_count: number;
  failed_count: number;
}

/**
 * List Invitations Query
 */
export interface ListInvitationsQuery {
  status?: string;
  team_id?: string;
  page?: number;
  limit?: number;
}

/**
 * Invitation Response
 */
export interface InvitationResponse {
  success: boolean;
  message: string;
  invitation?: Invitation;
  membership?: any;
}

/**
 * Invitation Service
 * 
 * Handles all invitation-related operations including:
 * - Creating and sending invitations
 * - Accepting and declining invitations
 * - Managing invitation lifecycle
 * - Listing and querying invitations
 * 
 * Extracted from the monolithic API service.
 */
export class InvitationService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== INVITATION CREATION =====

  /**
   * Create an invitation for an organization
   */
  async createInvitation(
    organizationId: string,
    data: CreateInvitationRequest
  ): Promise<Invitation> {
    try {
      console.log('Creating invitation for organization:', organizationId, 'with data:', data);
      
      const requestBody = {
        invitee_email: data.email,
        role: data.role,
        team_id: data.team_id,
        message: data.message,
        expires_at: data.expires_at
      };
      
      const response = await this.post<any>(
        `/organizations/${organizationId}/invitations`,
        requestBody,
        { timeout: 10000 }
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return this.normalizeInvitation(response);
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.createInvitation');
    }
  }

  /**
   * Bulk invite members via the batch endpoint.
   * Supports both organization-level and team-level invites (pass teamId for team context).
   */
  async bulkInvite(
    organizationId: string,
    emails: string[],
    roleId: string,
    teamId?: string,
    message?: string
  ): Promise<BulkInviteResult> {
    try {
      const invitations = emails.map((email, index) => ({
        invitee_email: email,
        role: roleId,
        ...(teamId ? { team_id: teamId } : {}),
        ...(message ? { message } : {}),
        index
      }));

      const response = await this.post<any>(
        `/organizations/${organizationId}/invitations/batch`,
        { invitations },
        { timeout: 30000 }
      );

      this.invalidateCache('organization');

      return {
        successful_invitations: (response.successful_invitations || []).map((item: any) => ({
          ...item,
          invitation: item.invitation ? this.normalizeInvitation(item.invitation) : undefined
        })),
        failed_invitations: response.failed_invitations || [],
        total_requested: response.total_requested ?? emails.length,
        successful_count: response.successful_count ?? (response.successful_invitations?.length || 0),
        failed_count: response.failed_count ?? (response.failed_invitations?.length || 0)
      };
    } catch (error) {
      throw this.errorHandler.handleError(error, 'InvitationService.bulkInvite');
    }
  }

  /**
   * Create a team invitation (extended version)
   */
  async createTeamInvitation(
    organizationId: string,
    teamId: string,
    data: CreateInvitationExtendedRequest
  ): Promise<Invitation> {
    try {
      console.log(`Creating team invitation for team ${teamId} in org ${organizationId}`);
      
      const requestBody = {
        invitee_email: data.invitee_email,
        role: data.role,
        team_id: teamId,
        personal_message: data.personal_message,
        expires_at: data.expires_at
      };
      
      const response = await this.post<any>(
        `/organizations/${organizationId}/invitations`,
        requestBody,
        { timeout: 10000 }
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return this.normalizeInvitation(response);
    } catch (error) {
      console.error('Error creating team invitation:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.createTeamInvitation');
    }
  }

  // ===== INVITATION RETRIEVAL =====

  /**
   * List invitations for an organization
   */
  async listInvitations(
    organizationId: string,
    query: ListInvitationsQuery = {}
  ): Promise<{
    data: Invitation[];
    pagination?: any;
  }> {
    try {
      console.log('Listing invitations for organization:', organizationId, 'with params:', query);

      const params = {
        status: query.status,
        team_id: query.team_id,
        page: query.page || 1,
        limit: query.limit || 10
      };

      // Use requestWithMetadata to get both data and metadata
      const response = await this.requestWithMetadata<any>(
        'GET',
        `/organizations/${organizationId}/invitations`,
        undefined,
        { params, timeout: 10000 }
      );

      return {
        data: Array.isArray(response.data) ? response.data.map(this.normalizeInvitation) :
              (response.data || (response as any).invitations || []).map(this.normalizeInvitation),
        pagination: response.metadata?.pagination
      };
    } catch (error) {
      console.error('Error listing invitations:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.listInvitations');
    }
  }

  /**
   * Get invitation by ID
   */
  async getInvitationById(invitationId: string): Promise<Invitation> {
    try {
      console.log('Fetching invitation by ID:', invitationId);
      
      const response = await this.get<any>(
        `/invitations/${invitationId}`,
        { timeout: 10000 }
      );
      
      return this.normalizeInvitation(response);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      throw this.errorHandler.handleError(error, `InvitationService.getInvitationById(${invitationId})`);
    }
  }

  /**
   * Get invitation by token
   */
  async getInvitationByToken(token: string): Promise<Invitation> {
    try {
      console.log('Fetching invitation by token:', token.substring(0, 8) + '...');
      
      const response = await this.get<any>(
        `/invitations/token/${token}`,
        { timeout: 10000 }
      );
      
      return this.normalizeInvitation(response);
    } catch (error) {
      console.error('Error fetching invitation by token:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.getInvitationByToken');
    }
  }

  /**
   * Get user's pending invitations
   */
  async getUserInvitations(
    status?: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{
    data: Invitation[];
    pagination?: any;
  }> {
    try {
      console.log('Fetching user invitations with params:', { status, page, limit });
      
      const params: any = { page, limit };
      if (status) params.status = status;
      
      const response = await this.get<any>(
        '/users/me/invitations',
        { params, timeout: 10000 }
      );
      
      const invitations = (response.data || response || []).map(this.normalizeInvitation);
      
      return {
        data: invitations,
        pagination: response.pagination
      };
    } catch (error) {
      console.error('Error fetching user invitations:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.getUserInvitations');
    }
  }

  /**
   * Get user's sent invitations
   */
  async getUserSentInvitations(
    status?: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{
    data: Invitation[];
    pagination?: any;
  }> {
    try {
      console.log('Fetching user sent invitations with params:', { status, page, limit });
      
      const params: any = { page, limit };
      if (status) params.status = status;
      
      const response = await this.get<any>(
        '/users/me/sent-invitations',
        { params, timeout: 10000 }
      );
      
      return {
        data: (response.data || response || []).map(this.normalizeInvitation),
        pagination: response.pagination
      };
    } catch (error) {
      console.error('Error fetching user sent invitations:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.getUserSentInvitations');
    }
  }

  // ===== INVITATION ACTIONS =====

  /**
   * Accept invitation by ID
   */
  async acceptInvitationById(invitationId: string): Promise<InvitationResponse> {
    try {
      console.log('Accepting invitation by ID:', invitationId);
      
      const response = await this.post<any>(
        `/invitations/${invitationId}/accept`,
        {},
        { timeout: 10000 }
      );
      
      // Invalidate caches
      this.invalidateCache('user');
      this.invalidateCache('organization');
      
      return {
        success: true,
        message: response.message || 'Invitation accepted successfully',
        invitation: response.invitation ? this.normalizeInvitation(response.invitation) : undefined,
        membership: response.membership
      };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw this.errorHandler.handleError(error, `InvitationService.acceptInvitationById(${invitationId})`);
    }
  }

  /**
   * Accept invitation by token
   */
  async acceptInvitationByToken(token: string): Promise<InvitationResponse> {
    try {
      console.log('Accepting invitation by token:', token.substring(0, 8) + '...');
      
      const response = await this.post<any>(
        `/invitations/token/${token}/accept`,
        {},
        { timeout: 10000 }
      );
      
      // Invalidate caches
      this.invalidateCache('user');
      this.invalidateCache('organization');
      
      return {
        success: true,
        message: response.message || 'Invitation accepted successfully',
        invitation: response.invitation ? this.normalizeInvitation(response.invitation) : undefined,
        membership: response.membership
      };
    } catch (error) {
      console.error('Error accepting invitation by token:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.acceptInvitationByToken');
    }
  }

  /**
   * Decline invitation by ID
   */
  async declineInvitationById(invitationId: string, reason?: string): Promise<InvitationResponse> {
    try {
      console.log('Declining invitation by ID:', invitationId, 'with reason:', reason);
      
      const response = await this.post<any>(
        `/invitations/${invitationId}/decline`,
        { reason },
        { timeout: 10000 }
      );
      
      // Invalidate cache
      this.invalidateCache('user');
      
      return {
        success: true,
        message: response.message || 'Invitation declined',
        invitation: response.invitation ? this.normalizeInvitation(response.invitation) : undefined
      };
    } catch (error) {
      console.error('Error declining invitation:', error);
      throw this.errorHandler.handleError(error, `InvitationService.declineInvitationById(${invitationId})`);
    }
  }

  /**
   * Decline invitation by token
   */
  async declineInvitationByToken(token: string, reason?: string): Promise<InvitationResponse> {
    try {
      console.log('Declining invitation by token:', token.substring(0, 8) + '...', 'with reason:', reason);
      
      const response = await this.post<any>(
        `/invitations/token/${token}/decline`,
        { reason },
        { timeout: 10000 }
      );
      
      // Invalidate cache
      this.invalidateCache('user');
      
      return {
        success: true,
        message: response.message || 'Invitation declined',
        invitation: response.invitation ? this.normalizeInvitation(response.invitation) : undefined
      };
    } catch (error) {
      console.error('Error declining invitation by token:', error);
      throw this.errorHandler.handleError(error, 'InvitationService.declineInvitationByToken');
    }
  }

  /**
   * Cancel invitation
   */
  async cancelInvitation(invitationId: string, reason?: string): Promise<InvitationResponse> {
    try {
      console.log('Cancelling invitation:', invitationId, 'with reason:', reason);
      
      const response = await this.delete<any>(
        `/invitations/${invitationId}`,
        {
          data: { reason },
          timeout: 10000
        }
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return {
        success: true,
        message: response.message || 'Invitation cancelled successfully'
      };
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      throw this.errorHandler.handleError(error, `InvitationService.cancelInvitation(${invitationId})`);
    }
  }

  /**
   * Resend invitation
   */
  async resendInvitation(invitationId: string): Promise<InvitationResponse> {
    try {
      console.log('Resending invitation:', invitationId);
      
      const response = await this.post<any>(
        `/invitations/${invitationId}/resend`,
        {},
        { timeout: 10000 }
      );
      
      return {
        success: true,
        message: response.message || 'Invitation resent successfully',
        invitation: response.invitation ? this.normalizeInvitation(response.invitation) : undefined
      };
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw this.errorHandler.handleError(error, `InvitationService.resendInvitation(${invitationId})`);
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Normalize invitation data from API response
   */
  private normalizeInvitation(invData: any): Invitation {
    return {
      id: invData.id || invData._id,
      organization_id: invData.organization_id || invData.organizationId,
      organization_name: invData.organization_name || invData.organizationName,
      team_id: invData.team_id || invData.teamId,
      team_name: invData.team_name || invData.teamName,
      invitee_email: invData.invitee_email || invData.inviteeEmail || invData.email,
      inviter_id: invData.inviter_id || invData.inviterId,
      inviter_name: invData.inviter_name || invData.inviterName,
      role: invData.role,
      role_id: invData.role_id || invData.roleId,
      role_name: invData.role_name || invData.roleName,
      status: invData.status || 'pending',
      personal_message: invData.personal_message || invData.personalMessage || invData.message,
      decline_reason: invData.decline_reason || invData.declineReason,
      token: invData.token,
      expires_at: invData.expires_at || invData.expiresAt,
      created_at: invData.created_at || invData.createdAt,
      accepted_at: invData.accepted_at || invData.acceptedAt,
      declined_at: invData.declined_at || invData.declinedAt,
      cancelled_at: invData.cancelled_at || invData.cancelledAt
    };
  }
}

// Export singleton instance
export const invitationService = new InvitationService();